import React, { useState, useEffect } from 'react';
import Header from './Header';
import DeploymentModal from './DeploymentModal';

const EssayGradingWindow = () => {
  const [essayText, setEssayText] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [studentName, setStudentName] = useState('');
  const [schoolLevel, setSchoolLevel] = useState('');
  const [grade, setGrade] = useState('');
  const [essayTopic, setEssayTopic] = useState('');
  const [isGrading, setIsGrading] = useState(false);
  const [gradingResult, setGradingResult] = useState(null);
  const [originalGradingResult, setOriginalGradingResult] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [tempEditValue, setTempEditValue] = useState('');
  const [error, setError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  
  // DeploymentModal 관련 state
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [deploymentData, setDeploymentData] = useState(null);

  // localStorage에서 저장된 설정 로드
  useEffect(() => {
    const loadSavedSettings = () => {
      try {
        const savedSettings = localStorage.getItem('user_default_settings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          if (parsedSettings.defaultSheetUrl) {
            setSheetUrl(parsedSettings.defaultSheetUrl);
            console.log('✅ 저장된 구글 시트 URL 로드됨:', parsedSettings.defaultSheetUrl);
          }
          if (parsedSettings.defaultSchoolLevel) {
            setSchoolLevel(parsedSettings.defaultSchoolLevel);
            console.log('✅ 저장된 학교급 로드됨:', parsedSettings.defaultSchoolLevel);
          }
          if (parsedSettings.defaultGrade) {
            setGrade(parsedSettings.defaultGrade);
            console.log('✅ 저장된 학년 로드됨:', parsedSettings.defaultGrade);
          }
        }
      } catch (error) {
        console.error('❌ 저장된 설정 로드 실패:', error);
      }
    };

    loadSavedSettings();
  }, []);

  // 편집 상태 디버깅
  useEffect(() => {
    console.log('편집 상태 변경:', { editingField, tempEditValue });
  }, [editingField, tempEditValue]);

  // 에세이 자동 채점 요청
  const handleGradeEssay = async () => {
    if (!essayText.trim()) {
      setError('에세이 내용을 입력해주세요.');
      return;
    }

    if (!studentName.trim()) {
      setError('학생 이름을 입력해주세요.');
      return;
    }

    setIsGrading(true);
    setError('');
    setGradingResult(null);

    try {
      const response = await fetch('/api/grade-essay', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          essay: essayText,
          studentName,
          schoolLevel,
          grade,
          topic: essayTopic,
          sheetUrl
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setGradingResult(result.grading);
        setOriginalGradingResult(JSON.parse(JSON.stringify(result.grading))); // 깊은 복사
        console.log('✅ 에세이 채점 완료:', result.grading);
      } else {
        throw new Error(result.error || '에세이 채점에 실패했습니다.');
      }
    } catch (error) {
      console.error('에세이 채점 오류:', error);
      setError(`채점 오류: ${error.message}`);
    } finally {
      setIsGrading(false);
    }
  };

  // 점수에 따른 색상 반환
  const getScoreColor = (score) => {
    if (score >= 4) return 'text-green-600 bg-green-50';
    if (score >= 3) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  // 총점 계산
  const getTotalScore = () => {
    if (!gradingResult?.scores) return 0;
    const { task_completion, content, organization, language_use } = gradingResult.scores;
    return task_completion + content + organization + language_use;
  };

  // 새 채점 시작
  const startNewGrading = () => {
    setEssayText('');
    setStudentName('');
    setEssayTopic('');
    setGradingResult(null);
    setOriginalGradingResult(null);
    setEditingField(null);
    setTempEditValue('');
    setError('');
    setIsExporting(false);
  };

  // 편집 시작
  const startEdit = (fieldType, fieldKey) => {
    // 기존 편집 중인 항목이 있으면 먼저 취소
    if (editingField) {
      setEditingField(null);
      setTempEditValue('');
    }
    
    const fieldId = `${fieldType}_${fieldKey}`;
    console.log('편집 시작:', { fieldType, fieldKey, fieldId });
    console.log('현재 gradingResult:', gradingResult);
    
    setEditingField(fieldId);
    
    if (fieldType === 'score') {
      const currentValue = gradingResult.scores[fieldKey];
      console.log('점수 편집 시작:', { fieldKey, currentValue });
      setTempEditValue(currentValue.toString());
    } else if (fieldType === 'feedback') {
      const currentValue = gradingResult.feedback[fieldKey];
      console.log('피드백 편집 시작:', { fieldKey, currentValue });
      setTempEditValue(currentValue || '');
    } else if (fieldType === 'overall') {
      setTempEditValue(gradingResult.overall_comment || '');
    } else if (fieldType === 'corrected') {
      setTempEditValue(gradingResult.corrected_version || '');
    }
  };

  // 편집 저장
  const saveEdit = () => {
    if (!editingField) return;
    
    // 첫 번째 언더스코어로만 분리 (task_completion, language_use 고려)
    const firstUnderscoreIndex = editingField.indexOf('_');
    const fieldType = editingField.substring(0, firstUnderscoreIndex);
    const fieldKey = editingField.substring(firstUnderscoreIndex + 1);
    
    console.log('편집 저장 - 분리 결과:', { editingField, fieldType, fieldKey });
    
    // 깊은 복사를 위해 JSON 방식 사용
    const newResult = JSON.parse(JSON.stringify(gradingResult));
    
    if (fieldType === 'score') {
      const score = parseInt(tempEditValue);
      if (score >= 1 && score <= 5) {
        newResult.scores[fieldKey] = score;
        console.log('점수 수정:', { fieldKey, oldValue: gradingResult.scores[fieldKey], newValue: score });
      } else {
        alert('점수는 1~5 사이의 숫자여야 합니다.');
        return;
      }
    } else if (fieldType === 'feedback') {
      newResult.feedback[fieldKey] = tempEditValue;
      console.log('피드백 수정:', { fieldKey, oldValue: gradingResult.feedback[fieldKey], newValue: tempEditValue });
    } else if (fieldType === 'overall') {
      newResult.overall_comment = tempEditValue;
    } else if (fieldType === 'corrected') {
      newResult.corrected_version = tempEditValue;
    }
    
    console.log('편집 저장 완료:', { fieldType, fieldKey, newValue: tempEditValue, newResult });
    
    setGradingResult(newResult);
    setEditingField(null);
    setTempEditValue('');
  };

  // 편집 취소
  const cancelEdit = () => {
    console.log('편집 취소');
    setEditingField(null);
    setTempEditValue('');
  };

  // 내보내기 기능
  const handleExport = async () => {
    if (isExporting) return; // 이미 처리 중이면 중복 실행 방지
    
    setIsExporting(true);
    setError(''); // 기존 오류 메시지 초기화
    
    try {
      const response = await fetch('/api/generate-essay-feedback-html', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentName,
          schoolLevel,
          grade,
          topic: essayTopic,
          essay: essayText,
          gradingResult,
          originalGradingResult,
          wasModified: JSON.stringify(gradingResult) !== JSON.stringify(originalGradingResult),
          sheetUrl
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setDeploymentData({
          htmlContent: result.html,
          fileName: result.fileName,
          contentType: 'essay-feedback'
        });
        setShowDeploymentModal(true);
      } else {
        throw new Error(result.error || '내보내기에 실패했습니다.');
      }
    } catch (error) {
      console.error('내보내기 오류:', error);
      setError(`내보내기 오류: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header 컴포넌트 */}
      <Header />
      
      {/* 페이지 제목 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <h1 className="text-2xl font-bold text-gray-900">✍️ 에세이 자동 채점</h1>
            <p className="text-sm text-gray-600 mt-1">AI가 에세이를 분석하여 자동으로 채점하고 피드백을 제공합니다</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!gradingResult ? (
          // 에세이 입력 및 채점 요청 섹션
          <div className="space-y-6">
            {/* 기본 정보 입력 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">기본 정보</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    학생 이름 *
                  </label>
                  <input
                    type="text"
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    placeholder="학생 이름을 입력하세요"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isGrading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    학교급
                  </label>
                  <select
                    value={schoolLevel}
                    onChange={(e) => setSchoolLevel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isGrading}
                  >
                    <option value="">선택하세요</option>
                    <option value="elementary">초등학교</option>
                    <option value="middle">중학교</option>
                    <option value="high">고등학교</option>
                    <option value="advanced">심화과정</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    학년
                  </label>
                  <select
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={!schoolLevel || isGrading}
                  >
                    <option value="">선택하세요</option>
                    {schoolLevel === 'elementary' && (
                      <>
                        <option value="3">3학년</option>
                        <option value="4">4학년</option>
                        <option value="5">5학년</option>
                        <option value="6">6학년</option>
                      </>
                    )}
                    {schoolLevel === 'middle' && (
                      <>
                        <option value="1">1학년</option>
                        <option value="2">2학년</option>
                        <option value="3">3학년</option>
                      </>
                    )}
                    {schoolLevel === 'high' && (
                      <>
                        <option value="1">1학년</option>
                        <option value="2">2학년</option>
                        <option value="3">3학년</option>
                      </>
                    )}
                    {schoolLevel === 'advanced' && (
                      <>
                        <option value="1">1단계</option>
                        <option value="2">2단계</option>
                        <option value="3">3단계</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    에세이 주제 (선택사항)
                  </label>
                  <input
                    type="text"
                    value={essayTopic}
                    onChange={(e) => setEssayTopic(e.target.value)}
                    placeholder="에세이 주제를 입력하세요"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isGrading}
                  />
                </div>
              </div>
            </div>

            {/* 구글 시트 URL */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">결과 저장</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  구글 시트 URL (선택사항)
                </label>
                <input
                  type="url"
                  value={sheetUrl}
                  onChange={(e) => setSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isGrading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  채점 결과를 저장할 구글시트 URL을 입력하세요 (에세이피드백 시트에 저장됩니다)
                </p>
              </div>
            </div>

            {/* 에세이 입력 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">에세이 내용</h2>
              <textarea
                value={essayText}
                onChange={(e) => setEssayText(e.target.value)}
                placeholder="채점할 에세이 내용을 입력하세요..."
                rows={15}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={isGrading}
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-gray-500">
                  글자 수: {essayText.length}자
                </p>
                <p className="text-sm text-gray-500">
                  단어 수: {essayText.trim() ? essayText.trim().split(/\s+/).length : 0}개
                </p>
              </div>
            </div>

            {/* 오류 메시지 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* 채점 버튼 */}
            <div className="text-center">
              <button
                onClick={handleGradeEssay}
                disabled={isGrading || !essayText.trim() || !studentName.trim()}
                className="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-lg font-semibold flex items-center gap-2 mx-auto"
              >
                {isGrading && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                )}
                {isGrading ? '채점 중...' : '✍️ 에세이 채점하기'}
              </button>
              <p className="text-sm text-gray-600 mt-2">
                AI가 에세이를 분석하여 자동으로 채점합니다 (약 10-30초 소요)
              </p>
            </div>
          </div>
        ) : (
          // 채점 결과 표시 섹션
          <div className="space-y-6">
            {/* 에러 메시지 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            
            {/* 채점 결과 헤더 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">채점 결과</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    학생: {studentName} {schoolLevel && grade && `| ${schoolLevel === 'elementary' ? '초등학교' : schoolLevel === 'middle' ? '중학교' : schoolLevel === 'high' ? '고등학교' : '심화과정'} ${grade}학년`} {essayTopic && `| 주제: ${essayTopic}`}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">
                    {getTotalScore()}/20
                  </div>
                  <p className="text-sm text-gray-500">총점</p>
                </div>
              </div>
            </div>

            {/* 세부 점수 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(gradingResult.scores).map(([key, score]) => {
                const titles = {
                  task_completion: '과제 완성도',
                  content: '내용',
                  organization: '구성',
                  language_use: '언어 사용'
                };
                const fieldId = `score_${key}`;
                const isEditing = editingField === fieldId;
                
                return (
                  <div key={`score-card-${key}`} className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium text-gray-700">{titles[key]}</h3>
                      <button
                        onClick={() => startEdit('score', key)}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                        title="점수 수정"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <input
                          type="number"
                          min="1"
                          max="5"
                          value={tempEditValue}
                          onChange={(e) => setTempEditValue(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={saveEdit}
                            className="flex-1 px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                          >
                            저장
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="flex-1 px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`text-2xl font-bold px-3 py-1 rounded-md ${getScoreColor(score)}`}>
                        {score}/5
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 상세 피드백 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                {Object.entries({task_completion: '📋 과제 완성도 피드백', content: '💡 내용 피드백'}).map(([key, title]) => {
                  const fieldId = `feedback_${key}`;
                  const isEditing = editingField === fieldId;
                  
                  return (
                    <div key={`feedback-left-${key}`} className="bg-white rounded-lg shadow-sm p-6">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-semibold text-gray-900">{title}</h3>
                        <button
                          onClick={() => startEdit('feedback', key)}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title="피드백 수정"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </div>
                      {isEditing ? (
                        <div className="space-y-3">
                          <textarea
                            value={tempEditValue}
                            onChange={(e) => setTempEditValue(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={4}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={saveEdit}
                              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                            >
                              저장
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                            >
                              취소
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-700 leading-relaxed">{gradingResult.feedback[key]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="space-y-4">
                {Object.entries({organization: '🏗️ 구성 피드백', language_use: '🔤 언어 사용 피드백'}).map(([key, title]) => {
                  const fieldId = `feedback_${key}`;
                  const isEditing = editingField === fieldId;
                  
                  return (
                    <div key={`feedback-right-${key}`} className="bg-white rounded-lg shadow-sm p-6">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-semibold text-gray-900">{title}</h3>
                        <button
                          onClick={() => startEdit('feedback', key)}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title="피드백 수정"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </div>
                      {isEditing ? (
                        <div className="space-y-3">
                          <textarea
                            value={tempEditValue}
                            onChange={(e) => setTempEditValue(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={4}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={saveEdit}
                              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                            >
                              저장
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                            >
                              취소
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-700 leading-relaxed">{gradingResult.feedback[key]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 종합 평가 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-900">📝 종합 평가</h3>
                <button
                  onClick={() => startEdit('overall', 'comment')}
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  title="종합 평가 수정"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </div>
              {editingField === 'overall_comment' ? (
                <div className="space-y-3">
                  <textarea
                    value={tempEditValue}
                    onChange={(e) => setTempEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                    >
                      저장
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                    >
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-700 leading-relaxed">{gradingResult.overall_comment}</p>
              )}
            </div>

            {/* 수정된 버전 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-900">✏️ 수정된 버전</h3>
                <button
                  onClick={() => startEdit('corrected', 'version')}
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  title="수정된 버전 편집"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </div>
              {editingField === 'corrected_version' ? (
                <div className="space-y-3">
                  <textarea
                    value={tempEditValue}
                    onChange={(e) => setTempEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={8}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                    >
                      저장
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                    >
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {gradingResult.corrected_version || '수정된 버전이 없습니다.'}
                  </p>
                </div>
              )}
            </div>

            {/* 버튼들 */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={startNewGrading}
                className="px-8 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-lg font-semibold"
              >
                📝 새 에세이 채점하기
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-lg font-semibold flex items-center gap-2 justify-center"
              >
                {isExporting && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                )}
                {isExporting ? '📤 내보내는 중...' : '📄 결과 내보내기'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DeploymentModal */}
      {showDeploymentModal && deploymentData && (
        <DeploymentModal
          isOpen={showDeploymentModal}
          htmlContent={deploymentData.htmlContent}
          fileName={deploymentData.fileName}
          contentType={deploymentData.contentType}
          onClose={() => setShowDeploymentModal(false)}
        />
      )}
    </div>
  );
};

export default EssayGradingWindow;